/**
 * Transaktionsdaten Tab für Schritt 2
 * Zeigt die importierten Transaktionsdaten in einer übersichtlichen Tabelle an
 */

import React, { useState, useMemo } from 'react';
import {
  Table,
  Header,
  SpaceBetween,
  Badge,
  Box,
  TextFilter,
  Pagination,
  CollectionPreferences,
  Alert,
  Container,
  FormField,
  Icon,
  StatusIndicator,
  Button
} from '@cloudscape-design/components';
// import { NonCancelableEventHandler } from '@cloudscape-design/components/internal/events';

import { TransaktionsDaten } from '@/types';
import AdvancedFilters, { FilterOptions } from '@/components/AdvancedFilters';

interface TransaktionsdatenTabProps {
  transaktionsDaten: TransaktionsDaten[];
  logosByAsset?: Record<string, string>;
  istVerfügbar: boolean;
}

/**
 * Komponente für die Anzeige der Transaktionsdaten
 */
const TransaktionsdatenTab: React.FC<TransaktionsdatenTabProps> = ({
  transaktionsDaten,
  logosByAsset,
  istVerfügbar
}) => {
  const [filterText, setFilterText] = useState('');
  const [advancedFilters, setAdvancedFilters] = useState<FilterOptions>({
    jahr: '',
    typ: '',
    wertpapier: '',
    portfolio: ''
  });
  const [sortingColumn, setSortingColumn] = useState<any>({ sortingField: 'date' });
  const [isDescending, setIsDescending] = useState(false);
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [preferences, setPreferences] = useState({
    pageSize: 100,
    visibleContent: ['identifier', 'date', 'type', 'name', 'portfolio', 'quantity', 'price', 'totalValue', 'currency']
  });
  
  // Verwende pageSize aus preferences
  const pageSize = preferences.pageSize;

  // Gefilterte und sortierte Daten
  const processedData = useMemo(() => {
    let filtered = transaktionsDaten;

    // Erweiterte Filter anwenden
    if (advancedFilters.jahr) {
      filtered = filtered.filter(item => 
        new Date(item.date).getFullYear().toString() === advancedFilters.jahr
      );
    }
    if (advancedFilters.typ) {
      filtered = filtered.filter(item => item.type === advancedFilters.typ);
    }
    if (advancedFilters.wertpapier) {
      filtered = filtered.filter(item => 
        (item.holdname || item.name) === advancedFilters.wertpapier
      );
    }
    if (advancedFilters.portfolio) {
      filtered = filtered.filter(item => item.portfolio === advancedFilters.portfolio);
    }

    // Textfilter anwenden
    if (filterText) {
      filtered = filtered.filter(item =>
        Object.values(item).some(value =>
          value && value.toString().toLowerCase().includes(filterText.toLowerCase())
        )
      );
    }

    // Sortierung anwenden
    if (sortingColumn.sortingField) {
      filtered = [...filtered].sort((a: any, b: any) => {
        const aVal = a[sortingColumn.sortingField];
        const bVal = b[sortingColumn.sortingField];
        
        let result = 0;
        if (aVal < bVal) result = -1;
        else if (aVal > bVal) result = 1;
        
        return isDescending ? -result : result;
      });
    }

    return filtered;
  }, [transaktionsDaten, filterText, advancedFilters, sortingColumn, isDescending]);

  // Paginierte Daten
  const paginatedData = useMemo(() => {
    const startIndex = (currentPageIndex - 1) * pageSize;
    return processedData.slice(startIndex, startIndex + pageSize);
  }, [processedData, currentPageIndex, pageSize]);

  /**
   * Formatiert den Transaktionstyp für die Anzeige
   */
  const formatTransaktionstyp = (typ: string) => {
    const typeConfig = {
      buy: { text: 'Kauf', variant: 'green' as const },
      sell: { text: 'Verkauf', variant: 'red' as const },
      transfer_in: { text: 'Transfer-In', variant: 'green' as const },
      transfer_out: { text: 'Transfer-Out', variant: 'red' as const },
      dividend: { text: 'Dividende', variant: 'blue' as const },
      interest: { text: 'Zinsen', variant: 'grey' as const }
    };
    
    const config = typeConfig[typ as keyof typeof typeConfig] || { text: typ, variant: 'grey' as const };
    return <Badge color={config.variant}>{config.text}</Badge>;
  };

  /**
   * Formatiert Währungsbeträge
   */
  const formatCurrency = (betrag: number, waehrung: string = 'EUR') => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: waehrung
    }).format(betrag);
  };

  /**
   * Formatiert Datum und kennzeichnet ungültige Daten
   */
  const formatDatum = (datum: string) => {
    try {
      let date: Date;
      
      // Erkenne deutsches Datumsformat DD.MM.YYYY und konvertiere es
      if (datum.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
        const [day, month, year] = datum.split('.');
        date = new Date(`${year}-${month}-${day}`);
      } else {
        // Verwende Standard-Parsing für ISO-Format
        date = new Date(datum);
      }
      
      // Prüfe auf ungültige Daten
      if (isNaN(date.getTime())) {
        return 'Ungültiges Datum';
      }
      // Prüfe auf unrealistische Daten (z.B. vor 1900 oder weit in der Zukunft)
      const year = date.getFullYear();
      if (year < 1900 || year > 2030) {
        return 'Ungültiges Datum';
      }
      return date.toLocaleDateString('de-DE');
    } catch {
      return 'Ungültiges Datum';
    }
  };

  // Tabellen-Spaltendefinition
  const columnDefinitions = [
    {
      id: 'identifier',
      header: 'ISIN',
      cell: (item: TransaktionsDaten) => item.identifier,
      sortingField: 'identifier',
      isRowHeader: true
    },
    {
      id: 'date',
      header: 'Datum',
      cell: (item: TransaktionsDaten) => (
        <div style={{ textAlign: 'right' }}>{formatDatum(item.date)}</div>
      ),
      sortingField: 'date'
    },
    {
      id: 'type',
      header: 'Typ',
      cell: (item: TransaktionsDaten) => formatTransaktionstyp(item.type),
      sortingField: 'type'
    },
    {
      id: 'name',
      header: 'Wertpapier',
      cell: (item: TransaktionsDaten) => {
        const name = item.holdname || item.name;
        const logoUrl = logosByAsset?.[item.identifier];
        if (!logoUrl) return name;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <img
              src={logoUrl}
              alt=""
              width={24}
              height={24}
              style={{ objectFit: 'contain', flexShrink: 0 }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <span>{name}</span>
          </div>
        );
      },
      sortingField: 'name'
    },
    {
      id: 'portfolio',
      header: 'Portfolio',
      cell: (item: TransaktionsDaten) => item.portfolio || '-',
      sortingField: 'portfolio'
    },
    {
      id: 'quantity',
      header: 'Anzahl',
      cell: (item: TransaktionsDaten) => (
        <div style={{ textAlign: 'right' }}>{item.quantity.toLocaleString('de-DE')}</div>
      ),
      sortingField: 'quantity'
    },
    {
      id: 'price',
      header: 'Preis',
      cell: (item: TransaktionsDaten) => (
        <div style={{ textAlign: 'right' }}>{formatCurrency(item.price, item.currency)}</div>
      ),
      sortingField: 'price'
    },
    {
      id: 'totalValue',
      header: 'Gesamtwert',
      cell: (item: TransaktionsDaten) => (
        <div style={{ textAlign: 'right' }}>{formatCurrency(item.totalValue, item.currency)}</div>
      ),
      sortingField: 'totalValue'
    },
    {
      id: 'currency',
      header: 'Währung',
      cell: (item: TransaktionsDaten) => item.currency,
      sortingField: 'currency'
    },
    {
      id: 'fees',
      header: 'Gebühren',
      cell: (item: TransaktionsDaten) => (
        <div style={{ textAlign: 'right' }}>{formatCurrency(item.fees, item.currency)}</div>
      ),
      sortingField: 'fees'
    },
    {
      id: 'tax',
      header: 'Steuern',
      cell: (item: TransaktionsDaten) => (
        <div style={{ textAlign: 'right' }}>{formatCurrency(item.tax, item.currency)}</div>
      ),
      sortingField: 'tax'
    }
  ];

  // Statistiken berechnen
  /*
  const statistiken = useMemo(() => {
    const gesamtTransaktionen = transaktionsDaten.length;
    const käufe = transaktionsDaten.filter(t => t.type === 'buy').length;
    const verkäufe = transaktionsDaten.filter(t => t.type === 'sell').length;
    const dividenden = transaktionsDaten.filter(t => t.type === 'dividend').length;
    const gesamtvolumen = transaktionsDaten.reduce((sum, t) => sum + Math.abs(t.totalValue), 0);
    const einzigartigeWertpapiere = new Set(transaktionsDaten.map(t => t.identifier)).size;

    return {
      gesamtTransaktionen,
      käufe,
      verkäufe,
      dividenden,
      gesamtvolumen,
      einzigartigeWertpapiere
    };
  }, [transaktionsDaten]);
  */

  if (!istVerfügbar) {
    return (
      <Alert type="warning">
        <strong>Keine Daten verfügbar</strong>
        <br />
        Bitte importieren Sie zunächst Transaktionsdaten im Tab &quot;Import&quot;.
      </Alert>
    );
  }

  if (transaktionsDaten.length === 0) {
    return (
      <Alert type="info">
        <strong>Keine Transaktionen gefunden</strong>
        <br />
        Die importierten Daten enthielten keine gültigen Transaktionsdaten.
      </Alert>
    );
  }

      return (
      <SpaceBetween direction="vertical" size="l">

        {/* Verbesserte Filter-Sektion */}
        <Container
          header={
            <Header 
              variant="h3"
              description="Durchsuchen und filtern Sie Ihre Transaktionen mit erweiterten Optionen"
              actions={
                <SpaceBetween direction="horizontal" size="s">
                  {(filterText || Object.values(advancedFilters).some(f => f !== '')) && (
                    <StatusIndicator type="info">
                      {processedData.length} von {transaktionsDaten.length} Transaktionen
                    </StatusIndicator>
                  )}
                  {(filterText || Object.values(advancedFilters).some(f => f !== '')) && (
                    <Button 
                      variant="link"
                      iconName="close"
                      onClick={() => {
                        setFilterText('');
                        setAdvancedFilters({ jahr: '', typ: '', wertpapier: '', portfolio: '' });
                      }}
                    >
                      Alle Filter zurücksetzen
                    </Button>
                  )}
                </SpaceBetween>
              }
            >
              <Icon name="search" /> Filter & Suche
            </Header>
          }
        >
          <SpaceBetween direction="vertical" size="l">
            {/* Volltext-Suche */}
            <SpaceBetween direction="vertical" size="m">
              <Box variant="h4">
                <Icon name="search" variant="subtle" /> Volltext-Suche
              </Box>
              <FormField
                description="Durchsuchen Sie alle Felder: ISIN, Wertpapiername, Portfolio, Datum, etc."
                stretch
              >
                <TextFilter
                  filteringText={filterText}
                  onChange={({ detail }) => setFilterText(detail.filteringText)}
                  filteringAriaLabel="Transaktionen durchsuchen"
                  filteringClearAriaLabel="Suchfilter löschen"
                  countText={`${processedData.length} Treffer`}
                />
              </FormField>
              
              {/* Suchvorschläge */}
              {!filterText && (
                <Box variant="small" color="text-body-secondary">
                  <SpaceBetween direction="horizontal" size="xs">
                    <span>Beispiele:</span>
                    <Button 
                      variant="inline-link" 
                      onClick={() => setFilterText('ETF')}
                    >
                      &quot;ETF&quot;
                    </Button>
                    <Button 
                      variant="inline-link" 
                      onClick={() => setFilterText('2024')}
                    >
                      &quot;2024&quot;
                    </Button>
                    <Button 
                      variant="inline-link" 
                      onClick={() => setFilterText('Trade Republic')}
                    >
                      &quot;Trade Republic&quot;
                    </Button>
                  </SpaceBetween>
                </Box>
              )}
            </SpaceBetween>

            {/* Erweiterte Filter */}
            <AdvancedFilters
              transaktionsDaten={transaktionsDaten}
              onFilterChange={setAdvancedFilters}
              activeFilters={advancedFilters}
            />
          </SpaceBetween>
        </Container>

      {/* Ergebnis-Header mit Aktionen */}
      {processedData.length !== transaktionsDaten.length && (
        <Container>
          <Box>
            <StatusIndicator type="info">
              <strong>{processedData.length}</strong> von <strong>{transaktionsDaten.length}</strong> Transaktionen werden angezeigt
            </StatusIndicator>
          </Box>
        </Container>
      )}

      {/* Haupttabelle */}
      <Table
        columnDefinitions={columnDefinitions}
        items={paginatedData}
        loadingText="Lade Transaktionsdaten..."
        sortingColumn={sortingColumn}
        sortingDescending={isDescending}
        onSortingChange={({ detail }) => {
          setSortingColumn(detail.sortingColumn);
          setIsDescending(detail.isDescending ?? false);
        }}
        visibleColumns={preferences.visibleContent}
        empty={
          <Box textAlign="center" color="inherit">
            <b>Keine Transaktionen gefunden</b>
            <Box padding={{ bottom: 's' }} variant="p" color="inherit">
              Versuchen Sie, den Suchfilter zu ändern.
            </Box>
          </Box>
        }

        header={
          <Header
            description="Alle importierten Transaktionen im Überblick"
          >
            Transaktionsdaten
          </Header>
        }
        pagination={
          <Pagination
            currentPageIndex={currentPageIndex}
            onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
            pagesCount={Math.ceil(processedData.length / pageSize)}
            ariaLabels={{
              nextPageLabel: 'Nächste Seite',
              previousPageLabel: 'Vorherige Seite',
              pageLabel: (pageNumber) => `Seite ${pageNumber} von allen Seiten`
            }}
          />
        }
        preferences={
          <CollectionPreferences
            title="Präferenzen"
            confirmLabel="Bestätigen"
            cancelLabel="Abbrechen"
            preferences={preferences}
            onConfirm={({ detail }) => {
              setPreferences(detail as any);
              // Reset zur ersten Seite wenn sich die Seitengröße ändert
              if (detail.pageSize !== preferences.pageSize) {
                setCurrentPageIndex(1);
              }
            }}
            pageSizePreference={{
              title: 'Seitengröße auswählen',
              options: [
                { value: 100, label: '100 Transaktionen' },
                { value: 200, label: '200 Transaktionen' },
                { value: 500, label: '500 Transaktionen' }
              ]
            }}
            visibleContentPreference={{
              title: 'Spalten auswählen',
              options: [
                {
                  label: 'Transaktionsdetails',
                  options: columnDefinitions.map(({ id, header }) => ({
                    id,
                    label: header
                  }))
                }
              ]
            }}
          />
        }
      />
    </SpaceBetween>
  );
};

export default TransaktionsdatenTab;